/**
 * Hierarchy Service
 * 
 * Provides core functions for managing the multi-level organization hierarchy.
 * Supports unlimited nesting of companies/divisions with permission control.
 * 
 * BACKWARD COMPATIBILITY: All functions gracefully handle companies without
 * hierarchy fields - they are treated as root-level companies.
 */

import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  writeBatch,
  serverTimestamp,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { db } from '../firebase';
import { isReservedCompanyId, reservedCompanyIdMessage } from '../utils/reservedCompanyIds';
import { orgAccessCompanyIds } from '../utils/orgAccess';

// ============================================
// CONSTANTS
// ============================================

export const HIERARCHY_DEFAULTS = {
  parentCompanyId: null,
  hierarchyPath: [],
  hierarchyLevel: 0,
  childCompanyIds: [],
  billingConfig: {
    mode: 'independent',
    rollupToCompanyId: null,
    separateInvoicing: true
  },
  inheritParentCarriers: false,
  inheritParentSettings: false,
  inheritParentCustomers: false
};

export const ACCESS_MODES = {
  FULL: 'full',           // See all descendants of root companies
  BRANCH: 'branch',       // See only specific branches
  SINGLE: 'single'        // See only the specific company (no children)
};

export const DATA_VISIBILITY = {
  OWN: 'own',             // Only data from user's direct company
  DESCENDANTS: 'descendants', // Data from company + all children
  ALL: 'all'              // All accessible companies in hierarchy
};

export const BILLING_MODES = {
  INDEPENDENT: 'independent',
  ROLLUP: 'rollup'
};

// ============================================
// COMPANY HIERARCHY FUNCTIONS
// ============================================

/**
 * Get a company with hierarchy fields (with defaults for backward compatibility)
 * @param {string} companyId - The companyID (business identifier)
 * @returns {Promise<Object|null>} Company data with hierarchy fields
 */
export const getCompanyWithHierarchy = async (companyId) => {
  try {
    const companiesRef = collection(db, 'companies');
    const q = query(companiesRef, where('companyID', '==', companyId));
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) return null;
    
    const companyDoc = snapshot.docs[0];
    const companyData = companyDoc.data();
    
    // Merge with defaults for backward compatibility
    return {
      id: companyDoc.id,
      ...HIERARCHY_DEFAULTS,
      ...companyData,
      // Ensure hierarchyPath includes self if not present
      hierarchyPath: companyData.hierarchyPath?.length > 0 
        ? companyData.hierarchyPath 
        : [companyData.companyID]
    };
  } catch (error) {
    console.error('Error getting company with hierarchy:', error);
    throw error;
  }
};

/**
 * Get all ancestor companies (parents up to root)
 * @param {string} companyId - The companyID to start from
 * @returns {Promise<Array>} Array of ancestor companies, from immediate parent to root
 */
export const getCompanyAncestors = async (companyId) => {
  try {
    const ancestors = [];
    let currentCompanyId = companyId;
    const visited = new Set(); // Prevent infinite loops
    
    while (currentCompanyId && !visited.has(currentCompanyId)) {
      visited.add(currentCompanyId);
      
      const company = await getCompanyWithHierarchy(currentCompanyId);
      if (!company) break;
      
      if (company.parentCompanyId) {
        const parent = await getCompanyWithHierarchy(company.parentCompanyId);
        if (parent) {
          ancestors.push(parent);
          currentCompanyId = parent.companyID;
        } else {
          break;
        }
      } else {
        break; // Reached root
      }
    }
    
    return ancestors;
  } catch (error) {
    console.error('Error getting company ancestors:', error);
    throw error;
  }
};

/**
 * Get all descendant companies (children recursively)
 * @param {string} companyId - The companyID to start from
 * @param {number} maxDepth - Maximum depth to traverse (null = unlimited)
 * @returns {Promise<Array>} Array of all descendant companies
 */
export const getCompanyDescendants = async (companyId, maxDepth = null) => {
  try {
    const descendants = [];
    const queue = [{ companyId, depth: 0 }];
    const visited = new Set();
    
    while (queue.length > 0) {
      const { companyId: currentId, depth } = queue.shift();
      
      if (visited.has(currentId)) continue;
      if (maxDepth !== null && depth > maxDepth) continue;
      
      visited.add(currentId);
      
      // Find all companies where parentCompanyId equals currentId
      const companiesRef = collection(db, 'companies');
      const q = query(companiesRef, where('parentCompanyId', '==', currentId));
      const snapshot = await getDocs(q);
      
      for (const doc of snapshot.docs) {
        const childCompany = { id: doc.id, ...doc.data() };
        descendants.push(childCompany);
        queue.push({ companyId: childCompany.companyID, depth: depth + 1 });
      }
    }
    
    return descendants;
  } catch (error) {
    console.error('Error getting company descendants:', error);
    throw error;
  }
};

/**
 * Get all companies in a hierarchy branch (from root to all leaves)
 * @param {string} rootCompanyId - The root companyID
 * @returns {Promise<Array>} Array of all companies in the branch
 */
export const getHierarchyBranch = async (rootCompanyId) => {
  try {
    const root = await getCompanyWithHierarchy(rootCompanyId);
    if (!root) return [];
    
    const descendants = await getCompanyDescendants(rootCompanyId);
    return [root, ...descendants];
  } catch (error) {
    console.error('Error getting hierarchy branch:', error);
    throw error;
  }
};

/**
 * Read ALL companies once (defaults merged). No orderBy — an orderBy('name') would silently
 * drop docs that lack a `name` field, so we sort in memory instead. Used by the superadmin
 * fast path in HierarchyContext to derive visibleCompanyIds + visibleCompanies + the tree from
 * a SINGLE read instead of three serial full-collection scans.
 * @returns {Promise<Array>} All company objects with hierarchy defaults merged
 */
export const getAllCompanies = async () => {
  const companiesRef = collection(db, 'companies');
  const snapshot = await getDocs(companiesRef);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...HIERARCHY_DEFAULTS,
    ...doc.data()
  }));
};

/**
 * Build a nested tree (roots with `children`) from a flat company array — pure, no I/O.
 * Extracted so callers that already hold the company list (e.g. the superadmin fast path)
 * can build the tree without re-reading Firestore.
 * @param {Array} companies - Flat array of company objects
 * @returns {Array} Root nodes, each with a `children` array, sorted by name
 */
export const buildHierarchyTreeFromCompanies = (companies) => {
  const companyMap = new Map();
  const roots = [];

  // First pass: create map
  companies.forEach(company => {
    companyMap.set(company.companyID, { ...company, children: [] });
  });

  // Second pass: build tree
  companies.forEach(company => {
    const node = companyMap.get(company.companyID);
    if (company.parentCompanyId && companyMap.has(company.parentCompanyId)) {
      companyMap.get(company.parentCompanyId).children.push(node);
    } else {
      roots.push(node);
    }
  });

  // Sort children by name (name-less nodes sort first rather than throwing)
  const sortChildren = (nodes) => {
    nodes.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    nodes.forEach(node => {
      if (node.children.length > 0) {
        sortChildren(node.children);
      }
    });
  };

  sortChildren(roots);

  return roots;
};

/**
 * Build a tree structure for UI rendering
 * @param {string} rootCompanyId - The root companyID (or null for all roots)
 * @returns {Promise<Object>} Tree structure with children nested
 */
export const getHierarchyTree = async (rootCompanyId = null) => {
  try {
    let companies = [];

    if (rootCompanyId) {
      // Get specific branch
      companies = await getHierarchyBranch(rootCompanyId);
    } else {
      // Get all companies
      const companiesRef = collection(db, 'companies');
      const snapshot = await getDocs(query(companiesRef, orderBy('name', 'asc')));
      companies = snapshot.docs.map(doc => ({
        id: doc.id,
        ...HIERARCHY_DEFAULTS,
        ...doc.data()
      }));
    }

    return buildHierarchyTreeFromCompanies(companies);
  } catch (error) {
    console.error('Error building hierarchy tree:', error);
    throw error;
  }
};

// ============================================
// USER ACCESS FUNCTIONS
// ============================================

/**
 * Get all companies a user can access based on their hierarchy settings
 * @param {Object} user - User object with hierarchy access settings
 * @returns {Promise<Array>} Array of companyIDs user can access
 */
export const getVisibleCompanyIds = async (user) => {
  try {
    if (!user) return [];
    
    const userRole = user.role || 'user';
    
    // Super admins can see everything
    if (userRole === 'superadmin') {
      const companiesRef = collection(db, 'companies');
      const snapshot = await getDocs(companiesRef);
      return snapshot.docs.map(doc => doc.data().companyID).filter(Boolean);
    }
    
    const hierarchyAccess = user.hierarchyAccess;
    // Default-SINGLE: descendant expansion is OPT-IN via an explicit accessMode
    // ('full'/'branch'). Users without one (the overwhelming default — the admin
    // UI mirrors rootCompanyIds on every save but never writes accessMode) get
    // EXACTLY their assigned companies. The old strict `=== SINGLE` check made
    // an absent accessMode expansive, so a customer-restricted user connected to
    // the master company could see its entire client tree (data leak). Matches
    // HierarchyContext's accessMode default.
    const isSingle = (hierarchyAccess?.accessMode || ACCESS_MODES.SINGLE) === ACCESS_MODES.SINGLE;
    const accessibleIds = new Set();

    // (1) Directly-connected companies — the UI-managed, authoritative assignment.
    // ALWAYS included so a stale / out-of-sync hierarchyAccess.rootCompanyIds can
    // never drop the companies the user is actually assigned to. (This was the
    // "dashboard shows zero while /shipments works" drift: the dashboard trusted
    // rootCompanyIds, which had silently diverged from connectedCompanies.)
    // Normalize both the `{ companies: [...] }` and bare-array shapes.
    const connectedCompanies = Array.isArray(user.connectedCompanies)
      ? user.connectedCompanies
      : (user.connectedCompanies?.companies || []);
    for (const companyId of connectedCompanies) {
      accessibleIds.add(companyId);
      if (!isSingle) {
        try {
          const descendants = await getCompanyDescendants(companyId);
          descendants.forEach(d => { if (d.companyID) accessibleIds.add(d.companyID); });
        } catch (err) {
          console.warn(`Could not get descendants for ${companyId}:`, err);
        }
      }
    }

    // (2) Hierarchy roots + descendants, when configured (additive to the above).
    if (hierarchyAccess && hierarchyAccess.rootCompanyIds?.length > 0) {
      const hierarchyIds = new Set();
      for (const rootId of hierarchyAccess.rootCompanyIds) {
        hierarchyIds.add(rootId);
        if (!isSingle) {
          // Same tolerance as the connected companies above: a descendant walk
          // the rules refuse (the parentCompanyId query is only provable for
          // billing roles) must not drop the companies the user does have.
          try {
            const descendants = await getCompanyDescendants(rootId);
            descendants.forEach(d => { if (d.companyID) hierarchyIds.add(d.companyID); });
          } catch (err) {
            console.warn(`Could not get descendants for ${rootId}:`, err);
          }
        }
      }
      // Branch mode restricts the HIERARCHY-derived ids to specific branches; the
      // directly-connected companies above are always retained.
      if (hierarchyAccess.accessMode === ACCESS_MODES.BRANCH &&
          hierarchyAccess.specificBranches?.length > 0) {
        const branchSet = new Set(hierarchyAccess.specificBranches);
        hierarchyIds.forEach(id => { if (branchSet.has(id)) accessibleIds.add(id); });
      } else {
        hierarchyIds.forEach(id => accessibleIds.add(id));
      }
    }

    // (3) Companies an organization access grant reaches (server-derived
    // orgAccess lists): member = full rights, view = read-only. Listed so the
    // company pickers and scoped reads reach them; writes stay with the rules.
    orgAccessCompanyIds(user).all.forEach(id => accessibleIds.add(id));

    return Array.from(accessibleIds);
  } catch (error) {
    console.error('Error getting visible company IDs:', error);
    throw error;
  }
};

/**
 * Get visible companies as full objects (for UI)
 * @param {Object} user - User object
 * @returns {Promise<Array>} Array of company objects user can access
 */
export const getVisibleCompanies = async (user, precomputedIds = null) => {
  try {
    // Reuse ids the caller already computed (HierarchyContext does) instead of recomputing the
    // whole visible-id set — which, for hierarchy users, re-runs the descendant BFS a second time.
    const companyIds = Array.isArray(precomputedIds) ? precomputedIds : await getVisibleCompanyIds(user);

    if (companyIds.length === 0) return [];
    
    // Fetch all companies in batches (Firestore 'in' query limit is 30), in parallel —
    // the batches are independent, so there's no reason to wait on each in turn.
    const batchSize = 30;
    const companiesRef = collection(db, 'companies');
    const batches = [];
    for (let i = 0; i < companyIds.length; i += batchSize) {
      batches.push(companyIds.slice(i, i + batchSize));
    }
    const snapshots = await Promise.all(
      batches.map(batch => getDocs(query(companiesRef, where('companyID', 'in', batch))))
    );

    const companies = [];
    snapshots.forEach(snapshot => {
      snapshot.docs.forEach(doc => {
        companies.push({
          id: doc.id,
          ...HIERARCHY_DEFAULTS,
          ...doc.data()
        });
      });
    });

    return companies;
  } catch (error) {
    console.error('Error getting visible companies:', error);
    throw error;
  }
};

/**
 * Check if a user can access a specific company
 * @param {Object} user - User object
 * @param {string} companyId - companyID to check access for
 * @returns {Promise<boolean>} Whether user can access the company
 */
export const canUserAccessCompany = async (user, companyId) => {
  try {
    if (!user || !companyId) return false;
    
    // Super admins can access everything
    if (user.role === 'superadmin') return true;
    
    const visibleIds = await getVisibleCompanyIds(user);
    return visibleIds.includes(companyId);
  } catch (error) {
    console.error('Error checking user access:', error);
    return false;
  }
};

/**
 * Check if a user can create a sub-division under a company
 * @param {Object} user - User object
 * @param {string} parentCompanyId - companyID of the parent
 * @returns {Promise<boolean>} Whether user can create under this parent
 */
export const canUserCreateUnder = async (user, parentCompanyId) => {
  try {
    if (!user) return false;
    
    // Super admins can always create
    if (user.role === 'superadmin') return true;
    
    // Check explicit permission
    if (!user.canCreateSubDivisions) return false;
    
    // Check if user can access the parent company
    const canAccess = await canUserAccessCompany(user, parentCompanyId);
    if (!canAccess) return false;
    
    // Check depth limit
    if (user.maxHierarchyDepth !== null && user.maxHierarchyDepth !== undefined) {
      const parent = await getCompanyWithHierarchy(parentCompanyId);
      if (parent && parent.hierarchyLevel >= user.maxHierarchyDepth) {
        return false;
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error checking create permission:', error);
    return false;
  }
};

// ============================================
// HIERARCHY MODIFICATION FUNCTIONS
// ============================================

/**
 * Create a new division/child company under a parent
 * @param {Object} companyData - New company data
 * @param {string} parentCompanyId - Parent companyID (or null for root)
 * @returns {Promise<Object>} Created company data
 */
export const createDivision = async (companyData, parentCompanyId = null) => {
  try {
    if (isReservedCompanyId(companyData?.companyID)) {
      throw new Error(reservedCompanyIdMessage(companyData.companyID));
    }
    const batch = writeBatch(db);
    
    let hierarchyPath = [companyData.companyID];
    let hierarchyLevel = 0;
    
    if (parentCompanyId) {
      const parent = await getCompanyWithHierarchy(parentCompanyId);
      if (!parent) {
        throw new Error('Parent company not found');
      }
      
      hierarchyPath = [...(parent.hierarchyPath || [parent.companyID]), companyData.companyID];
      hierarchyLevel = (parent.hierarchyLevel || 0) + 1;
      
      // Update parent's childCompanyIds
      const parentDocRef = doc(db, 'companies', parent.id);
      batch.update(parentDocRef, {
        childCompanyIds: arrayUnion(companyData.companyID),
        updatedAt: serverTimestamp()
      });
    }
    
    // Create new company with hierarchy fields
    const newCompanyRef = doc(collection(db, 'companies'));
    const newCompanyData = {
      ...HIERARCHY_DEFAULTS,
      ...companyData,
      parentCompanyId,
      hierarchyPath,
      hierarchyLevel,
      childCompanyIds: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    batch.set(newCompanyRef, newCompanyData);
    
    await batch.commit();
    
    return {
      id: newCompanyRef.id,
      ...newCompanyData
    };
  } catch (error) {
    console.error('Error creating division:', error);
    throw error;
  }
};

/**
 * Move a company to a new parent (reparenting)
 * @param {string} companyId - companyID to move
 * @param {string} newParentCompanyId - New parent companyID (or null for root)
 * @returns {Promise<void>}
 */
export const moveCompany = async (companyId, newParentCompanyId) => {
  try {
    const company = await getCompanyWithHierarchy(companyId);
    if (!company) {
      throw new Error('Company not found');
    }
    
    const batch = writeBatch(db);
    
    // Remove from old parent's childCompanyIds
    if (company.parentCompanyId) {
      const oldParent = await getCompanyWithHierarchy(company.parentCompanyId);
      if (oldParent) {
        const oldParentRef = doc(db, 'companies', oldParent.id);
        batch.update(oldParentRef, {
          childCompanyIds: arrayRemove(companyId),
          updatedAt: serverTimestamp()
        });
      }
    }
    
    // Calculate new hierarchy path and level
    let newHierarchyPath = [companyId];
    let newHierarchyLevel = 0;
    
    if (newParentCompanyId) {
      const newParent = await getCompanyWithHierarchy(newParentCompanyId);
      if (!newParent) {
        throw new Error('New parent company not found');
      }
      
      // Prevent circular reference
      if (newParent.hierarchyPath?.includes(companyId)) {
        throw new Error('Cannot move company to its own descendant');
      }
      
      newHierarchyPath = [...(newParent.hierarchyPath || [newParent.companyID]), companyId];
      newHierarchyLevel = (newParent.hierarchyLevel || 0) + 1;
      
      // Add to new parent's childCompanyIds
      const newParentRef = doc(db, 'companies', newParent.id);
      batch.update(newParentRef, {
        childCompanyIds: arrayUnion(companyId),
        updatedAt: serverTimestamp()
      });
    }
    
    // Update the company itself
    const companyRef = doc(db, 'companies', company.id);
    batch.update(companyRef, {
      parentCompanyId: newParentCompanyId,
      hierarchyPath: newHierarchyPath,
      hierarchyLevel: newHierarchyLevel,
      updatedAt: serverTimestamp()
    });
    
    await batch.commit();
    
    // Update all descendants' hierarchy paths
    await updateDescendantPaths(companyId, newHierarchyPath, newHierarchyLevel);
  } catch (error) {
    console.error('Error moving company:', error);
    throw error;
  }
};

/**
 * Update hierarchy paths for all descendants after a move
 * @param {string} companyId - companyID that was moved
 * @param {Array} newParentPath - New parent's hierarchy path
 * @param {number} newParentLevel - New parent's hierarchy level
 */
const updateDescendantPaths = async (companyId, newParentPath, newParentLevel) => {
  try {
    const descendants = await getCompanyDescendants(companyId);
    
    if (descendants.length === 0) return;
    
    // Process in batches (Firestore batch limit is 500)
    const batchSize = 400;
    
    for (let i = 0; i < descendants.length; i += batchSize) {
      const batch = writeBatch(db);
      const batchDescendants = descendants.slice(i, i + batchSize);
      
      for (const descendant of batchDescendants) {
        // Find position of companyId in old path and replace everything before
        const oldPath = descendant.hierarchyPath || [];
        const companyIndex = oldPath.indexOf(companyId);
        
        if (companyIndex !== -1) {
          const newPath = [...newParentPath, ...oldPath.slice(companyIndex)];
          const newLevel = newParentLevel + (oldPath.length - companyIndex);
          
          const descendantRef = doc(db, 'companies', descendant.id);
          batch.update(descendantRef, {
            hierarchyPath: newPath,
            hierarchyLevel: newLevel,
            updatedAt: serverTimestamp()
          });
        }
      }
      
      await batch.commit();
    }
  } catch (error) {
    console.error('Error updating descendant paths:', error);
    throw error;
  }
};

/**
 * Update billing configuration for a company
 * @param {string} companyId - companyID to update
 * @param {Object} billingConfig - New billing configuration
 * @returns {Promise<void>}
 */
export const updateBillingConfig = async (companyId, billingConfig) => {
  try {
    const company = await getCompanyWithHierarchy(companyId);
    if (!company) {
      throw new Error('Company not found');
    }
    
    const companyRef = doc(db, 'companies', company.id);
    await writeBatch(db).update(companyRef, {
      billingConfig: {
        ...HIERARCHY_DEFAULTS.billingConfig,
        ...billingConfig
      },
      updatedAt: serverTimestamp()
    }).commit();
  } catch (error) {
    console.error('Error updating billing config:', error);
    throw error;
  }
};

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Get the hierarchy path as a human-readable string
 * @param {Array} hierarchyPath - Array of companyIDs
 * @returns {Promise<string>} Path string like "Parent > Child > Grandchild"
 */
export const getHierarchyPathDisplay = async (hierarchyPath) => {
  try {
    if (!hierarchyPath || hierarchyPath.length === 0) return '';
    
    const names = [];
    for (const companyId of hierarchyPath) {
      const company = await getCompanyWithHierarchy(companyId);
      if (company) {
        names.push(company.name);
      }
    }
    
    return names.join(' > ');
  } catch (error) {
    console.error('Error getting hierarchy path display:', error);
    return '';
  }
};

/**
 * Check if one company is an ancestor of another
 * @param {string} ancestorId - Potential ancestor companyID
 * @param {string} descendantId - Potential descendant companyID
 * @returns {Promise<boolean>}
 */
export const isAncestorOf = async (ancestorId, descendantId) => {
  try {
    const descendant = await getCompanyWithHierarchy(descendantId);
    if (!descendant) return false;
    
    return descendant.hierarchyPath?.includes(ancestorId) || false;
  } catch (error) {
    console.error('Error checking ancestry:', error);
    return false;
  }
};

/**
 * Get all companyIDs that should be included in data queries for a user
 * Based on their data visibility settings
 * @param {Object} user - User object
 * @param {string} currentCompanyId - Currently selected company
 * @returns {Promise<Array>} Array of companyIDs to include in queries
 */
export const getQueryCompanyIds = async (user, currentCompanyId) => {
  try {
    if (!user) return [];
    
    // Super admins can query everything
    if (user.role === 'superadmin') return null; // null means no filter
    
    const dataVisibility = user.hierarchyAccess?.dataVisibility || DATA_VISIBILITY.OWN;
    
    switch (dataVisibility) {
      case DATA_VISIBILITY.OWN:
        return currentCompanyId ? [currentCompanyId] : [];
        
      case DATA_VISIBILITY.DESCENDANTS:
        if (!currentCompanyId) return [];
        const descendants = await getCompanyDescendants(currentCompanyId);
        return [currentCompanyId, ...descendants.map(d => d.companyID)];
        
      case DATA_VISIBILITY.ALL:
        return await getVisibleCompanyIds(user);
        
      default:
        return currentCompanyId ? [currentCompanyId] : [];
    }
  } catch (error) {
    console.error('Error getting query company IDs:', error);
    return currentCompanyId ? [currentCompanyId] : [];
  }
};

/**
 * Get the companyIDs that belong to an organization. This is the foundation for
 * org-level roll-up reporting and org scoping.
 * Primary source is the reverse link (companies.organizationID); falls back to
 * the organization's connectedCompanies array when the reverse link isn't set
 * yet (e.g. before the backfill-company-organizationID migration has run).
 * @param {string} orgID - Organization business id (orgID)
 * @returns {Promise<string[]>} De-duplicated array of companyIDs
 */
export const getOrganizationCompanyIds = async (orgID) => {
  if (!orgID) return [];
  try {
    const companiesRef = collection(db, 'companies');
    const snap = await getDocs(query(companiesRef, where('organizationID', '==', orgID)));
    const ids = snap.docs.map(d => d.data().companyID).filter(Boolean);
    if (ids.length > 0) return Array.from(new Set(ids));

    // Fallback: read connectedCompanies off the organization doc
    const orgSnap = await getDocs(query(collection(db, 'organizations'), where('orgID', '==', orgID)));
    if (!orgSnap.empty) {
      const connected = orgSnap.docs[0].data().connectedCompanies || [];
      return Array.from(new Set(connected.filter(Boolean)));
    }
    return [];
  } catch (error) {
    console.error('Error getting organization company IDs:', error);
    return [];
  }
};

/**
 * Flatten a hierarchy tree into an array with indentation levels
 * Useful for rendering in select dropdowns
 * @param {Array} tree - Tree from getHierarchyTree()
 * @param {number} level - Current indentation level
 * @returns {Array} Flattened array with level property
 */
export const flattenHierarchyTree = (tree, level = 0) => {
  const result = [];
  
  for (const node of tree) {
    result.push({
      ...node,
      level,
      children: undefined // Remove children from flattened result
    });
    
    if (node.children && node.children.length > 0) {
      result.push(...flattenHierarchyTree(node.children, level + 1));
    }
  }
  
  return result;
};

// ============================================
// EXPORT DEFAULT SERVICE OBJECT
// ============================================

const hierarchyService = {
  // Constants
  HIERARCHY_DEFAULTS,
  ACCESS_MODES,
  DATA_VISIBILITY,
  BILLING_MODES,
  
  // Company hierarchy functions
  getCompanyWithHierarchy,
  getCompanyAncestors,
  getCompanyDescendants,
  getHierarchyBranch,
  getHierarchyTree,
  getAllCompanies,
  buildHierarchyTreeFromCompanies,
  
  // User access functions
  getVisibleCompanyIds,
  getVisibleCompanies,
  canUserAccessCompany,
  canUserCreateUnder,
  
  // Modification functions
  createDivision,
  moveCompany,
  updateBillingConfig,
  
  // Utility functions
  getHierarchyPathDisplay,
  isAncestorOf,
  getQueryCompanyIds,
  getOrganizationCompanyIds,
  flattenHierarchyTree
};

export default hierarchyService;
